"use client";

import { useMemo, useState } from "react";
import {
  Loader2,
  ChevronRight,
  ChevronDown,
  CheckCircle2,
  Circle,
  Clock,
  AlertCircle,
  Check,
  X,
  Code2,
} from "lucide-react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/cjs/styles/prism";
import { cn } from "@/lib/utils";
import { isRecord } from "@/utils/isRecord";
import {
  resolveToolDisplay,
  resolveApprovalLabel,
  type ToolDisplayInfo,
} from "./toolDisplay";
import {
  declaresCard,
  resolveApproval,
  rowTargetOf,
} from "./approval/approvalRegistry";
import { CardTarget, EntityCardHeader } from "./approval/EntityApprovalBody";
import {
  decisionWord,
  deniedWith,
  getApprovalDecision,
} from "./approval/approvalGate";
import {
  humanizeFieldLabel,
  resolveIdField,
} from "./approval/ArgsApprovalBody";
import { EntityNamesProvider, lookupFromPart } from "./entityNames";
import { useMcpPrefixMeta } from "./mcpToolIcon";
import { Badge } from "@/components/shadcn/badge";
import { CopyButton } from "@/components/UI/CopyButton";
import { useChatDisplayOptionsContext } from "./useChatDisplayOptions";
import {
  CHAIN_TEXT_SIZE,
  useInChainOfThought,
} from "../ai-elements/chain-of-thought";
import { useHoldScroll } from "../ai-elements/conversation";

export type ToolState =
  | "input-streaming"
  | "input-available"
  | "running"
  | "complete"
  | "output-available"
  | "output-denied"
  | "output-error"
  | "approval-requested"
  | "approval-responded"
  | "error";

export type ToolPartBase = {
  type: string;
  toolCallId: string;
  state: ToolState;
  input?: unknown;
  output?: unknown;
  errorText?: string;
  providerExecuted?: boolean;
  callProviderMetadata?: {
    noxus?: Record<string, unknown>;
  };
  // Where the AI SDK files a tool-input-error's metadata: a call refused
  // before it ran carries its display here, and only here.
  resultProviderMetadata?: {
    noxus?: Record<string, unknown>;
  };
};

interface BaseToolProps {
  part: ToolPartBase;
  info: ToolDisplayInfo;
  // Always under the row (a workflow's live progress).
  customContent?: React.ReactNode;
  // Under the row once it is opened (a script and its result).
  details?: React.ReactNode;
  defaultOpen?: boolean;
}

export const STEP_DETAIL_INDENT = "ml-2 border-l border-border pl-4";

export function StepDetail({ children }: { children: React.ReactNode }) {
  return <div className={cn("mt-1.5", STEP_DETAIL_INDENT)}>{children}</div>;
}

function PayloadBlock({ value }: { value: unknown }) {
  const code = useMemo(() => asCode(value), [value]);
  if (!code) return null;
  return (
    <div className="relative rounded border border-border bg-muted/50">
      <CopyButton
        value={code}
        className="absolute top-1 right-1 p-1 hover:bg-muted"
      />
      <pre className="whitespace-pre-wrap break-words px-2.5 py-1.5 pr-8 font-mono text-xs leading-relaxed text-muted-foreground">
        {code}
      </pre>
    </div>
  );
}

function PayloadDetails({ children }: { children: React.ReactNode }) {
  return <div className="max-h-72 space-y-1.5 overflow-y-auto">{children}</div>;
}

function asCode(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^[[{]/.test(trimmed)) {
      try {
        return JSON.stringify(JSON.parse(trimmed), null, 2);
      } catch {}
    }
    return value;
  }
  return JSON.stringify(value, null, 2);
}

// When the tool required approval, ``info.approval`` carries the user's
// decision — rendered as a small check (allowed) / cross (denied) badge on the
// tool's icon. Bottom right: a row outside a chain sits flush against the
// message's clipped left edge, which would cut a left-hanging badge.
function IconWithApprovalBadge({
  icon,
  approval,
}: {
  icon: React.ReactNode;
  approval?: { approved: boolean };
}) {
  if (!approval) return <>{icon}</>;
  return (
    <span className="relative inline-flex">
      {icon}
      <span
        className={cn(
          "absolute -bottom-1 -right-1 flex h-2.5 w-2.5 items-center justify-center rounded-full ring-1 ring-background",
          approval.approved ? "bg-emerald-500" : "bg-red-500"
        )}
      >
        {approval.approved ? (
          <Check className="h-1.5 w-1.5 text-white" strokeWidth={4} />
        ) : (
          <X className="h-1.5 w-1.5 text-white" strokeWidth={4} />
        )}
      </span>
    </span>
  );
}

// The user's words on a rejected call, under its row: Genie's next reply
// answers them, so a reopened chat must show them.
function DeniedNote({ part }: { part: ToolPartBase }) {
  const note = deniedWith(part);
  if (!note) return null;
  return (
    <p className="mt-0.5 pl-6 text-xs text-muted-foreground break-words">
      Rejected: &ldquo;{note}&rdquo;
    </p>
  );
}

// Why a call failed: the SDK's error text, else the reason the tool reported.
// A refusal is written for the model: the tool's name and error code before
// the reason, and pydantic-ai's retry instruction after it. The row shows the
// reason alone.
const MODEL_FRAMING = [
  // A tool name always has an underscore, so "timeout: …" stays.
  /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+: /,
  /^\[[a-z_]+\] /,
  /\n+Fix the errors and try again\.?\s*$/,
];

function failureReason(part: ToolPartBase): string | undefined {
  const reported = part.callProviderMetadata?.noxus?.["error_message"];
  const reason =
    part.errorText || (typeof reported === "string" ? reported : undefined);
  return reason
    ? MODEL_FRAMING.reduce((text, framing) => text.replace(framing, ""), reason)
    : undefined;
}

function FailureReason({ reason }: { reason: string }) {
  return (
    <div className="rounded border border-border bg-muted/50 px-2.5 py-1.5 text-xs text-red-500/90 whitespace-pre-wrap break-words">
      {reason}
    </div>
  );
}

export function BaseTool({
  part,
  info,
  customContent,
  details,
  defaultOpen = false,
}: BaseToolProps) {
  const { toolDisplayPreset, rowsShowChanges } = useChatDisplayOptionsContext();
  const compact = toolDisplayPreset === "execution";
  const inChain = useInChainOfThought();
  const rowText = inChain ? CHAIN_TEXT_SIZE : "text-sm";
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const holdScroll = useHoldScroll();
  const toggle = () => {
    holdScroll();
    setIsOpen(!isOpen);
  };
  const isRunning =
    part.state === "running" ||
    part.state === "input-streaming" ||
    part.state === "input-available";
  // A call that failed or was refused reads as failed, not done: the SDK's
  // output-error, or a server refusal the stream returns as a tool result.
  const isError =
    part.state === "error" ||
    part.state === "output-error" ||
    part.callProviderMetadata?.noxus?.["error"] === true;

  const icon = info.icon;
  const titleText = isRunning
    ? info.messageStart
    : isError
      ? info.messageError
      : info.messageEnd;
  const subtitleText = info.subtitle;

  // A decided call opens on what it changed, as its approval card showed it;
  // where rows show changes, so does a call that did not ask.
  const approvalPlan = resolveApproval(part);
  const showsChange = info.approval || (rowsShowChanges && declaresCard(part));
  const target = showsChange && !isError ? rowTargetOf(part) : undefined;
  const recapContent =
    showsChange && approvalPlan?.hasBody ? (
      <EntityNamesProvider part={part}>
        {approvalPlan.body()}
      </EntityNamesProvider>
    ) : null;
  const planBody =
    !isRunning && !isError && (recapContent || target) ? (
      <div className="rounded-lg border border-border bg-white overflow-hidden">
        {target && (
          <EntityNamesProvider part={part}>
            <EntityCardHeader entity={target} />
          </EntityNamesProvider>
        )}
        {recapContent && (
          <CardTarget.Provider value={target}>
            <div className="px-3 py-2.5">{recapContent}</div>
          </CardTarget.Provider>
        )}
      </div>
    ) : null;
  const reason = isError ? failureReason(part) : undefined;
  // What opening the row shows.
  const opened =
    planBody ??
    (details || reason ? (
      <div className="space-y-1.5">
        {details}
        {reason && <FailureReason reason={reason} />}
      </div>
    ) : null);

  // Compact / execution preset: inline pill, no expandable IO. Rich custom
  // content (e.g. the workflow progress steps) still renders — that's the
  // step-by-step execution detail this preset is meant to surface.
  if (compact) {
    return (
      <div data-tool-row>
        <button
          onClick={opened ? toggle : undefined}
          aria-expanded={opened ? isOpen : undefined}
          disabled={!opened}
          className={cn(
            "flex items-center gap-2 text-muted-foreground w-full min-w-0 text-left",
            rowText,
            opened && "cursor-pointer hover:text-foreground transition-colors"
          )}
        >
          <span
            className={cn(
              "shrink-0 inline-flex items-center",
              isError && "text-red-500"
            )}
          >
            {isRunning ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <IconWithApprovalBadge icon={icon} approval={info.approval} />
            )}
          </span>
          <span className={cn("min-w-0 truncate", isError && "text-red-500")}>
            {titleText}
          </span>
          {subtitleText && (
            <span className="font-mono truncate max-w-[200px]">
              · {subtitleText}
            </span>
          )}
          {opened && (
            <ChevronDown
              className={cn(
                "size-4 shrink-0 transition-transform",
                isOpen && "rotate-180"
              )}
            />
          )}
        </button>
        <DeniedNote part={part} />
        {customContent && <StepDetail>{customContent}</StepDetail>}
        {isOpen && opened && <StepDetail>{opened}</StepDetail>}
      </div>
    );
  }

  const hasOutput =
    typeof part.output === "string" ? part.output !== "" : part.output != null;

  return (
    <div data-tool-row className={rowText}>
      <button
        onClick={toggle}
        aria-expanded={isOpen}
        className="flex items-center gap-2 text-muted-foreground w-full min-w-0 text-left cursor-pointer hover:text-foreground transition-colors"
      >
        <span className="shrink-0 inline-flex items-center">
          {isRunning ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : isError ? (
            <AlertCircle className="h-3.5 w-3.5 text-red-500" />
          ) : (
            <span className="opacity-70 inline-flex items-center">
              <IconWithApprovalBadge icon={icon} approval={info.approval} />
            </span>
          )}
        </span>
        <span className={cn("truncate min-w-0", isError && "text-red-500")}>
          {titleText}
        </span>
        <ChevronDown
          className={cn(
            "size-4 shrink-0 transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </button>
      <DeniedNote part={part} />

      {(isOpen || isRunning) && customContent && (
        <StepDetail>{customContent}</StepDetail>
      )}

      {isOpen && !customContent && planBody && (
        <StepDetail>{planBody}</StepDetail>
      )}

      {isOpen && details && <StepDetail>{details}</StepDetail>}

      {isOpen &&
        !customContent &&
        !planBody &&
        !details &&
        (part.input != null || (hasOutput && !isRunning)) && (
          <StepDetail>
            <PayloadDetails>
              {part.input != null && <PayloadBlock value={part.input} />}
              {hasOutput && !isRunning && <PayloadBlock value={part.output} />}
            </PayloadDetails>
          </StepDetail>
        )}

      {reason && (
        <StepDetail>
          <FailureReason reason={reason} />
        </StepDetail>
      )}
    </div>
  );
}

type TodoStatus = "pending" | "in_progress" | "completed" | "blocked";

export type ParsedTodo = {
  status: TodoStatus;
  content: string;
};

const TODO_STATUS_CONFIG: Record<
  TodoStatus,
  {
    icon: React.ReactNode;
    badgeVariant: "secondary" | "warning" | "success" | "destructive";
    label: string;
  }
> = {
  pending: {
    icon: <Circle className="h-3.5 w-3.5 text-muted-foreground" />,
    badgeVariant: "secondary",
    label: "Pending",
  },
  in_progress: {
    icon: (
      <Clock className="h-3.5 w-3.5 text-yellow-600 dark:text-yellow-400" />
    ),
    badgeVariant: "warning",
    label: "In Progress",
  },
  completed: {
    icon: (
      <CheckCircle2 className="h-3.5 w-3.5 text-success-600 dark:text-success-400" />
    ),
    badgeVariant: "success",
    label: "Done",
  },
  blocked: {
    icon: (
      <AlertCircle className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
    ),
    badgeVariant: "destructive",
    label: "Blocked",
  },
};

export function TodoRow({ todo }: { todo: ParsedTodo }) {
  const config = TODO_STATUS_CONFIG[todo.status] ?? TODO_STATUS_CONFIG.pending;
  return (
    <div className="flex items-start gap-2 py-0.5">
      <span className="shrink-0 mt-0.5">{config.icon}</span>
      <span
        className={cn(
          "text-sm flex-1 min-w-0",
          todo.status === "completed" && "line-through text-muted-foreground"
        )}
      >
        {todo.content}
      </span>
      <Badge variant={config.badgeVariant} className="text-[10px] px-1.5 py-0">
        {config.label}
      </Badge>
    </div>
  );
}

// --- Tool Approval Card ---

export type ToolApprovalPart = ToolPartBase & {
  approval?: { id: string; approved?: boolean; reason?: string };
};

// Display-only: the approve/deny actions live in the composer
// (ApprovalComposer), so the inline card just shows what's being approved
// and, once answered, the Allowed/Rejected outcome. Icon and label come from
// ``resolveToolDisplay`` so the card, the composer, and the running-tool row
// all read the same.
export function ToolApprovalCard({ part }: { part: ToolApprovalPart }) {
  const [showRaw, setShowRaw] = useState(false);
  const names = useMemo(() => lookupFromPart(part), [part]);

  const { icon } = resolveToolDisplay(part, useMcpPrefixMeta());

  const isResponded = part.state === ("approval-responded" as ToolState);
  const decision = isResponded ? getApprovalDecision(part) : undefined;
  const wasApproved = decision?.approved === true;

  const inputObj =
    typeof part.input === "object" && part.input !== null
      ? (part.input as Record<string, unknown>)
      : null;
  const secondaryEntries = inputObj ? Object.entries(inputObj).slice(1) : [];

  const approval = resolveApproval(part);

  // Same label the composer shows, so the card and the prompt read identically.
  const headerLabel = resolveApprovalLabel(part);

  const inputPreview =
    part.input != null
      ? typeof part.input === "string"
        ? part.input
        : JSON.stringify(part.input, null, 2)
      : null;

  const isPendingApproval = part.state === "approval-requested";
  const hasBody = !!inputPreview;
  const showBody = !approval && hasBody && !isResponded;

  return (
    <div className="my-2 rounded-lg border border-border bg-white overflow-hidden text-sm">
      <div
        className={cn(
          "flex items-center gap-3 px-3 py-3",
          showBody && "border-b border-border"
        )}
      >
        <div
          className={cn(
            "h-6 w-6 rounded flex items-center justify-center shrink-0",
            isResponded
              ? wasApproved
                ? "bg-emerald-500/10"
                : "bg-red-400/10"
              : "bg-primary/10"
          )}
        >
          {isResponded ? (
            wasApproved ? (
              <Check className="size-3 text-emerald-500" />
            ) : (
              <X className="size-3 text-red-400" />
            )
          ) : (
            <span className="text-primary">{icon}</span>
          )}
        </div>
        <span className="min-w-0 truncate font-medium text-foreground text-[12px] leading-snug">
          {isResponded
            ? `${decisionWord(decision ?? { approved: false })} · ${headerLabel}`
            : headerLabel}
        </span>
        {isPendingApproval && (
          <span className="ml-auto flex shrink-0 items-center gap-1.5 rounded-full border border-warning-100 bg-warning-50 px-2 py-0.5 text-[11px] font-medium text-warning-800">
            <span className="size-1.5 animate-pulse rounded-full bg-warning-400" />
            Awaiting approval
          </span>
        )}
      </div>
      {isResponded && !wasApproved && (
        <div className="px-3 pb-2.5 -mt-1.5">
          <DeniedNote part={part} />
        </div>
      )}

      {showBody && (
        <div className="px-3 py-2.5">
          {secondaryEntries.length > 0 && (
            <div className="space-y-2.5 text-xs mb-2.5">
              {secondaryEntries.map(([key, value]) => {
                const named = resolveIdField(names, key, value);
                return (
                  <div key={key} className="flex flex-col gap-0.5">
                    <span className="text-foreground font-medium">
                      {named ? named.label : humanizeFieldLabel(key)}
                    </span>
                    <span className="text-foreground/80 break-words line-clamp-2">
                      {named
                        ? named.name
                        : typeof value === "string"
                          ? value
                          : JSON.stringify(value)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          <button
            onClick={() => setShowRaw(!showRaw)}
            className="flex items-center gap-1 text-xs text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
          >
            <ChevronRight
              className={cn(
                "size-3 transition-transform",
                showRaw && "rotate-90"
              )}
            />
            <Code2 className="size-3" />
            View raw payload
          </button>
          {showRaw && (
            <pre className="mt-1.5 rounded bg-background2 px-2.5 py-2 font-mono text-[11px] leading-relaxed text-muted-foreground whitespace-pre-wrap overflow-x-auto max-h-28">
              {inputPreview}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

// --- Code Execution Tool ---

type CodeExecutionOutput = {
  result?: string | number | boolean | null;
  stdout?: string;
  error?: string;
};

// The sandbox reports {result, stdout, error}. Code mode's run_code returns
// {output, result} when the script printed, else its value as is, whose own
// keys (a failed document's error) say nothing about the script.
function codeOutput(type: string, output: unknown): CodeExecutionOutput {
  if (type === "tool-run_code") {
    const printed =
      isRecord(output) &&
      typeof output.output === "string" &&
      Object.keys(output).every((key) => key === "output" || key === "result");
    return printed
      ? {
          stdout: String(output.output) || undefined,
          ...valueOf(output.result),
        }
      : valueOf(output);
  }
  if (
    isRecord(output) &&
    ("result" in output ||
      "stdout" in output ||
      typeof output.error === "string")
  )
    return output as CodeExecutionOutput;
  return valueOf(output);
}

function valueOf(value: unknown): CodeExecutionOutput {
  return value == null || value === ""
    ? {}
    : { result: typeof value === "string" ? value : asCode(value) };
}

function CodeSection({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
        {label}
      </div>
      {children}
    </div>
  );
}

type CodeExecutionToolCallProps = {
  part: ToolPartBase;
  // The rows of the tools the script called (code mode), each drawn as a
  // direct call of its tool.
  calls?: React.ReactNode[];
};

export function CodeExecutionToolCall({
  part,
  calls,
}: CodeExecutionToolCallProps) {
  const info = resolveToolDisplay(part);
  const code = isRecord(part.input) ? String(part.input.code ?? "") : "";
  const output = codeOutput(part.type, part.output);
  const result =
    output.result == null || output.result === ""
      ? ""
      : typeof output.result === "string"
        ? asCode(output.result)
        : JSON.stringify(output.result, null, 2);

  const details = (
    <div className="space-y-2 text-sm">
      {calls && calls.length > 0 && (
        <CodeSection label="Called">
          <div className="space-y-1">{calls}</div>
        </CodeSection>
      )}
      {code && (
        <CodeSection label="Code">
          <SyntaxHighlighter
            language="python"
            style={oneLight}
            customStyle={{
              margin: 0,
              borderRadius: "0.375rem",
              fontSize: "0.8rem",
            }}
          >
            {code}
          </SyntaxHighlighter>
        </CodeSection>
      )}
      {output.stdout && (
        <CodeSection label="Output">
          <div className="bg-background border rounded p-2 font-mono whitespace-pre-wrap overflow-x-auto max-h-60 overflow-y-auto">
            {output.stdout}
          </div>
        </CodeSection>
      )}
      {output.error && <FailureReason reason={output.error} />}
      {result && (
        <CodeSection label="Result">
          <div className="bg-background border rounded p-2 font-mono whitespace-pre-wrap overflow-x-auto max-h-60 overflow-y-auto">
            {result}
          </div>
        </CodeSection>
      )}
    </div>
  );

  return <BaseTool part={part} info={info} details={details} />;
}
